import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Spinner } from '@picsio/ui';
import cn from 'classnames';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { useSelector } from 'react-redux';
import getDownloadUrl from '../../helpers/getDownloadUrl';
import isNonBrowserRenderableImageMime from '../../helpers/assets/isNonBrowserRenderableImageMime';
import localization from '../../shared/strings';
import Logger from '../../services/Logger';
import cropToNormalized, {
  cropNaturalPixelSize,
  isFullImageCrop,
} from '../../helpers/assets/cropToNormalized';
import naturalPixelsToPercentCrop from '../../helpers/assets/naturalPixelsToPercentCrop';

// Keep in sync with:
//   picsio/static/assets/js/helpers/assets/copyCropAspectPresets.js (BULK_COPY_CROP_ASPECT_KEYS)
//   server/app/helpers/hasValidCropAspect.js
//   zipper/src/Converter/helpers/normalizedCropFromAspect.js (ASPECT_W_OVER_H)
//   zipper/src/Converter/helpers/computeCropRegion.js (parsePresetAspectRatio)
const ASPECT_BY_KEY = {
  '1:1': 1,
  '4:3': 4 / 3,
  '3:4': 3 / 4,
  '4:5': 4 / 5,
  '16:9': 16 / 9,
  '9:16': 9 / 16,
  '1.91:1': 1.91,
  '2.27:1': 2.27,
  '3:1': 3,
  '4:1': 4,
  '5.9:1': 5.9,
};

function buildCenteredAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 100,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

function buildFullPercentCrop(mediaWidth, mediaHeight) {
  return centerCrop(
    {
      unit: '%',
      width: 100,
      height: 100,
    },
    mediaWidth,
    mediaHeight,
  );
}

function cropForAspectKey(key, naturalWidth, naturalHeight) {
  if (key === 'original' || key === 'free') {
    return buildFullPercentCrop(naturalWidth, naturalHeight);
  }
  const aspect = ASPECT_BY_KEY[key];
  if (!aspect) {
    return buildFullPercentCrop(naturalWidth, naturalHeight);
  }
  return buildCenteredAspectCrop(naturalWidth, naturalHeight, aspect);
}

function reactCropAspectProp(key) {
  if (key === 'original' || key === 'free') return undefined;
  return ASPECT_BY_KEY[key];
}

function rasterThumbnailUrl(asset) {
  if (!asset?.thumbnail) return null;
  return asset.thumbnail.big || asset.thumbnail.default || asset.thumbnail.small || null;
}

const AssetsCopyCropPreview = forwardRef((props, ref) => {
  const {
    previewAssetId,
    sourceMimeType,
    aspectPreset,
    cropMode,
    onNormalizedCropChange,
    onCropPixelSizeChange,
    onSourceNaturalSizeChange,
    onUserAdjustedCrop,
    variant,
    lockResize,
  } = props;

  const catalogAsset = useSelector((state) => (
    previewAssetId
      ? state.assets.items.find((a) => a._id === previewAssetId)
      : undefined
  ));

  const imgRef = useRef(null);
  const onNormRef = useRef(onNormalizedCropChange);
  onNormRef.current = onNormalizedCropChange;
  const onPixelRef = useRef(onCropPixelSizeChange);
  onPixelRef.current = onCropPixelSizeChange;
  const onSourceNaturalRef = useRef(onSourceNaturalSizeChange);
  onSourceNaturalRef.current = onSourceNaturalSizeChange;
  const onUserAdjRef = useRef(onUserAdjustedCrop);
  onUserAdjRef.current = onUserAdjustedCrop;

  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cropImageDisplayed, setCropImageDisplayed] = useState(false);
  const [crop, setCrop] = useState();

  const publishCrop = useCallback((nextCrop, img) => {
    const el = img || imgRef.current;
    if (!el?.naturalWidth) {
      onPixelRef.current?.(null);
      return;
    }
    if (!nextCrop) {
      onNormRef.current(null);
      onPixelRef.current?.({
        width: el.naturalWidth,
        height: el.naturalHeight,
      });
      return;
    }
    const norm = cropToNormalized(nextCrop, el);
    if (!norm || isFullImageCrop(norm)) {
      onNormRef.current(null);
    } else {
      onNormRef.current(norm);
    }
    const px = cropNaturalPixelSize(nextCrop, el);
    onPixelRef.current?.(px || {
      width: el.naturalWidth,
      height: el.naturalHeight,
    });
  }, []);

  const applyAspectToImage = useCallback((img) => {
    if (!img?.naturalWidth) return;
    const { naturalWidth, naturalHeight } = img;
    onSourceNaturalRef.current?.({
      width: naturalWidth,
      height: naturalHeight,
    });
    const next = cropForAspectKey(aspectPreset, naturalWidth, naturalHeight);
    setCrop(next);
    publishCrop(next, img);
  }, [aspectPreset, publishCrop]);

  const applyFromNaturalPixels = useCallback((w, h) => {
    const img = imgRef.current;
    if (!img?.naturalWidth || !img?.naturalHeight) return;
    const next = naturalPixelsToPercentCrop(w, h, img.naturalWidth, img.naturalHeight);
    setCrop(next);
    publishCrop(next, img);
  }, [publishCrop]);

  useImperativeHandle(ref, () => ({
    applyFromNaturalPixels,
  }), [applyFromNaturalPixels]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!previewAssetId) {
        setPreviewUrl(null);
        setCrop(undefined);
        onNormRef.current(null);
        onPixelRef.current?.(null);
        onSourceNaturalRef.current?.(null);
        return;
      }
      setLoading(true);
      setLoadError(false);
      setPreviewUrl(null);
      setCrop(undefined);
      onSourceNaturalRef.current?.(null);
      try {
        const mime = sourceMimeType || catalogAsset?.mimeType;
        const ext = catalogAsset?.fileExtension;
        const needsRasterThumb = isNonBrowserRenderableImageMime(mime, ext);
        const thumbUrl = needsRasterThumb ? rasterThumbnailUrl(catalogAsset) : null;
        if (thumbUrl) {
          if (!cancelled) setPreviewUrl(thumbUrl);
          return;
        }

        const data = await getDownloadUrl({
          assetId: previewAssetId,
          allowDownloadByGS: false,
          forPreviewDownload: true,
        });
        const url = typeof data === 'string' ? data : data?.url;
        if (!cancelled) {
          if (url) setPreviewUrl(url);
          else setLoadError(true);
        }
      } catch (err) {
        Logger.error(new Error('AssetsCopyCropPreview: failed to load preview URL'), { err, previewAssetId }, [
          'AssetsCopyCropPreviewUrlFailed',
        ]);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [previewAssetId]);

  useEffect(() => {
    if (!previewUrl) setCropImageDisplayed(false);
  }, [previewUrl]);

  const onImageLoad = useCallback((e) => {
    setCropImageDisplayed(true);
    applyAspectToImage(e.currentTarget);
  }, [applyAspectToImage]);

  useEffect(() => {
    if (cropMode !== 'preset') return;
    const img = imgRef.current;
    if (!img?.naturalWidth) return;
    applyAspectToImage(img);
  }, [aspectPreset, cropMode, applyAspectToImage]);

  const handleCropChange = useCallback(
    (c) => {
      onUserAdjRef.current?.();
      setCrop(c);
      publishCrop(c, imgRef.current);
    },
    [publishCrop],
  );

  const { COPY_ASSETS_DIALOG: L } = localization;
  const isSplitPreview = variant === 'splitPreview';

  if (!previewAssetId) {
    return (
      <div className="assetsCopyDialog__cropHint">
        {L.cropBulkHint}
      </div>
    );
  }

  const rootClass = isSplitPreview
    ? 'assetsCopyDialog__crop assetsCopyDialog__crop--previewOnly'
    : 'assetsCopyDialog__crop';

  return (
    <div className={rootClass}>
      {!isSplitPreview && (
        <>
          <div className="assetsCopyDialog__cropLabel">{L.cropSectionTitle}</div>
          <div className="assetsCopyDialog__formatLabel">{L.cropRatioLabel}</div>
        </>
      )}
      <div
        className={cn('assetsCopyDialog__cropPreviewWrap', {
          'assetsCopyDialog__cropPreviewWrap--imageReady': cropImageDisplayed,
          'assetsCopyDialog__cropPreviewWrap--loading': loading,
        })}
      >
        {loading && (
          <div className="assetsCopyDialog__cropSpinner">
            <Spinner text={L.cropLoading} style={{ background: 'none' }} />
          </div>
        )}
        {loadError && !loading && (
          <div className="assetsCopyDialog__cropStatus assetsCopyDialog__cropStatus_error">{L.cropLoadError}</div>
        )}
        {previewUrl && !loadError && (
          <ReactCrop
            crop={crop}
            onChange={handleCropChange}
            aspect={reactCropAspectProp(aspectPreset)}
            className="assetsCopyDialog__reactCrop"
            ruleOfThirds
            disabled={aspectPreset === 'original'}
            locked={lockResize && aspectPreset !== 'original'}
          >
            <img
              ref={imgRef}
              className="assetsCopyDialog__cropImg"
              alt=""
              src={previewUrl}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        )}
      </div>
    </div>
  );
});

AssetsCopyCropPreview.propTypes = {
  previewAssetId: PropTypes.string,
  /**
   * With Redux asset, selects a pre-rendered raster thumbnail when the source file is not
   * natively renderable by browsers in an <img>: HEIC/HEIF, camera RAW formats (ARW, CR2,
   * CR3, DNG, NEF, ORF, RAF, RW2, etc.) and TIFF.
   */
  sourceMimeType: PropTypes.string,
  aspectPreset: PropTypes.string.isRequired,
  cropMode: PropTypes.oneOf(['preset', 'manual']).isRequired,
  onNormalizedCropChange: PropTypes.func.isRequired,
  onCropPixelSizeChange: PropTypes.func,
  /** Fires with `{ width, height }` in source pixels once the preview image is decoded, or `null` when there is no preview. */
  onSourceNaturalSizeChange: PropTypes.func,
  onUserAdjustedCrop: PropTypes.func,
  variant: PropTypes.oneOf(['stacked', 'splitPreview']),
  /** When true, crop rect can be dragged but not resized (used for default social presets). */
  lockResize: PropTypes.bool,
};

AssetsCopyCropPreview.defaultProps = {
  previewAssetId: undefined,
  sourceMimeType: undefined,
  onCropPixelSizeChange: undefined,
  onSourceNaturalSizeChange: undefined,
  onUserAdjustedCrop: undefined,
  variant: 'stacked',
  lockResize: false,
};

export default AssetsCopyCropPreview;
